import React from 'react';
import { Button } from './ui/button';
import { Check, ArrowRight, Zap, Users, Calendar, BarChart3, Clock, Bot, Database, Brain, Globe, Sparkles, Smartphone, Monitor, Code, Headphones } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const PackagesSection = ({ onBookNow }) => {
  const { isDarkMode } = useTheme();
  
  // Custom Website Features
  const websiteFeatures = [
    "Custom designed website",
    "SEO optimised",
    "Mobile responsive",
    "Google Business Profile creation",
    "Fast loading & secure",
    "Contact form integration"
  ];

  // Custom Software Features
  const softwareFeatures = [
    "Mobile app development (iOS & Android)",
    "Desktop software development",
    "Cross-platform solutions",
    "API integrations",
    "Database design & management",
    "Ongoing maintenance & updates"
  ];

  const softwareTypes = [
    { icon: Smartphone, name: "Mobile Apps", description: "Native iOS and Android apps, or cross-platform solutions using React Native" },
    { icon: Monitor, name: "Desktop Software", description: "Windows, Mac, and Linux applications tailored to your business needs" },
    { icon: Code, name: "Custom Solutions", description: "Bespoke software built to your exact specifications with AI-assisted development" }
  ];

  // AI Revenue System Features
  const revenueAgents = [
    { icon: Bot, name: "AI Sales Agent", description: "Engages new enquiries, answers questions, identifies buying intent and moves prospects towards a conversion." },
    { icon: Users, name: "AI Lead Qualification Agent", description: "Automatically qualifies prospects against your ideal customer criteria and prioritises opportunities." },
    { icon: Clock, name: "AI Follow-Up Agent", description: "Follows up with leads who don't respond, keeping opportunities alive without manual chasing." },
    { icon: Calendar, name: "AI Appointment Agent", description: "Books calls, consultations, meetings and appointments directly into your calendar." },
    { icon: Headphones, name: "AI Receptionist Agent", description: "Handles routine customer enquiries and provides instant answers across your website." },
    { icon: Database, name: "CRM Agent", description: "Automatically records conversations, updates lead information and keeps your pipeline organised." },
    { icon: Brain, name: "AI Knowledge Base", description: "Your agents are trained on your business, services, FAQs and processes." },
    { icon: BarChart3, name: "Performance Intelligence", description: "See how many enquiries are being handled, qualified, booked and converted." }
  ];

  const revenueIncludes = [
    "Multiple AI Agents",
    "Custom business knowledge",
    "CRM & calendar integrations",
    "Website & lead capture integration",
    "Automated follow-up",
    "Monthly optimisation",
    "Performance reporting",
    "Ongoing support"
  ];

  return (
    <section id="packages" className={`py-16 sm:py-24 transition-colors duration-300 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`} data-testid="packages-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block px-4 py-2 rounded-full text-sm font-bold mb-6 bg-yellow-500 text-black">
            AI SALES SYSTEMS FOR UK BUSINESSES
          </span>
          <h2 className={`text-3xl sm:text-4xl md:text-5xl font-black mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Stop Missing Enquiries. Start Booking Customers.
          </h2>
          <p className={`text-lg sm:text-xl max-w-3xl mx-auto ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            We don't give you another AI tool and leave you to figure it out.
            <br className="hidden sm:block" />
            <span className="font-semibold">We build, launch, manage and continuously optimise for you.</span>
          </p>
        </div>

        {/* Three Products Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          
          {/* PRODUCT 1: CUSTOM WEBSITE */}
          <div className="relative">
            <div className={`h-full rounded-3xl overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'} border-2 border-gray-300 dark:border-gray-700 shadow-lg`}>
              
              {/* Header */}
              <div className={`px-6 py-5 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <Globe className={`w-6 h-6 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`} />
                  <h3 className={`font-black text-lg sm:text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>CUSTOM WEBSITE</h3>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Professional web presence for your business</p>
              </div>

              {/* Pricing Options */}
              <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
                <div className="space-y-4">
                  {/* Monthly Option */}
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'} border`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-2xl sm:text-3xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          £100<span className="text-sm font-medium text-gray-400"> / month</span>
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Domain included</p>
                      </div>
                      <Sparkles className="w-6 h-6 text-yellow-500" />
                    </div>
                  </div>
                  
                  {/* One-time Option */}
                  <div className="text-center">
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>OR</p>
                  </div>
                  
                  <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`text-2xl sm:text-3xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          £599<span className="text-sm font-medium text-gray-400"> one-time</span>
                        </p>
                        <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>You buy your own domain</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-700">
                <h4 className={`font-bold text-sm mb-4 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>WHAT'S INCLUDED</h4>
                <div className="space-y-3">
                  {websiteFeatures.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                      <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Best For */}
              <div className={`px-6 py-4 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-semibold">Best for:</span> Small businesses and startups needing a professional online presence.
                </p>
              </div>

              {/* CTA */}
              <div className="px-6 py-5">
                <Button
                  onClick={onBookNow}
                  variant="outline"
                  className={`w-full font-bold py-3 rounded-full text-sm transition-all ${isDarkMode ? 'border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black' : 'border-yellow-600 text-yellow-600 hover:bg-yellow-500 hover:text-black hover:border-yellow-500'}`}
                  data-testid="website-cta"
                >
                  Get Your Website
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* PRODUCT 2: CUSTOM SOFTWARE */}
          <div className="relative">
            <div className={`h-full rounded-3xl overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'} border-2 border-purple-500/50 shadow-lg`}>
              
              {/* Header */}
              <div className={`px-6 py-5 bg-gradient-to-r ${isDarkMode ? 'from-purple-900 to-purple-800' : 'from-purple-100 to-purple-50'}`}>
                <div className="flex items-center gap-3 mb-2">
                  <Code className={`w-6 h-6 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  <h3 className={`font-black text-lg sm:text-xl ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>CUSTOM SOFTWARE</h3>
                </div>
                <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>Mobile Apps & Desktop Development</p>
              </div>

              {/* Description */}
              <div className="px-6 py-4 border-b border-purple-500/20">
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Tell us what software you need. We build custom mobile apps and desktop applications tailored to your exact requirements.
                </p>
              </div>

              {/* Software Types */}
              <div className="px-6 py-4 border-b border-purple-500/20">
                <h4 className={`font-bold text-xs mb-3 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>WHAT WE BUILD</h4>
                <div className="space-y-2">
                  {softwareTypes.map((type, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded bg-purple-500/20 flex items-center justify-center mt-0.5">
                        <type.icon className="w-3 h-3 text-purple-500" />
                      </div>
                      <div>
                        <p className={`font-semibold text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{type.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{type.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Disclosure Badge */}
              <div className={`px-6 py-3 ${isDarkMode ? 'bg-purple-500/10' : 'bg-purple-50'} border-b border-purple-500/20`}>
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-purple-500" />
                  <p className={`text-xs font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                    AI-assisted development for faster delivery
                  </p>
                </div>
              </div>

              {/* Features */}
              <div className="px-6 py-4 border-b border-purple-500/20">
                <div className="space-y-2">
                  {softwareFeatures.map((feature, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Check className="w-3 h-3 text-purple-500 flex-shrink-0" />
                      <span className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div className="px-6 py-4 border-b border-purple-500/20">
                <div className="text-center">
                  <p className={`text-lg font-bold ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    Custom Quote
                  </p>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Based on your requirements</p>
                </div>
              </div>

              {/* Best For */}
              <div className={`px-6 py-3 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-semibold">Best for:</span> Businesses needing bespoke software solutions.
                </p>
              </div>

              {/* CTA */}
              <div className="px-6 py-5">
                <Button
                  onClick={onBookNow}
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-3 rounded-full text-sm transition-all"
                  data-testid="software-cta"
                >
                  Discuss Your Project
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          {/* PRODUCT 3: AI REVENUE SYSTEM - HERO OFFER */}
          <div className="relative">
            {/* Recommended Badge */}
            <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
              <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-gradient-to-r from-yellow-500 to-yellow-400 text-black shadow-lg shadow-yellow-500/30">
                RECOMMENDED
              </span>
            </div>
            
            <div className={`h-full rounded-3xl overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'} border-2 border-yellow-500 shadow-xl shadow-yellow-500/20`}>
              
              {/* Header */}
              <div className="bg-gradient-to-r from-yellow-500 to-yellow-400 px-6 py-5">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-6 h-6 text-black" />
                  <h3 className="text-black font-black text-lg sm:text-xl">AI REVENUE SYSTEM</h3>
                </div>
                <p className="text-black/80 font-medium text-sm">Your AI Sales Team. Working 24/7.</p>
              </div>

              {/* Description */}
              <div className="px-6 py-4 border-b border-yellow-500/20">
                <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Turn more enquiries into customers with AI Agents that respond instantly, qualify prospects, follow up automatically and book appointments.
                </p>
              </div>

              {/* Pricing */}
              <div className="px-6 py-5 border-b border-yellow-500/20">
                <div className="text-center">
                  <p className={`text-3xl sm:text-4xl font-black ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    £1,497<span className="text-base font-medium text-gray-400"> / month</span>
                  </p>
                </div>
              </div>

              {/* Agents List - Condensed */}
              <div className="px-6 py-4 border-b border-yellow-500/20 max-h-64 overflow-y-auto">
                <h4 className="text-yellow-500 font-bold text-xs mb-3 uppercase tracking-wide">What's Included</h4>
                <div className="space-y-2">
                  {revenueAgents.map((agent, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-6 h-6 rounded bg-yellow-500/20 flex items-center justify-center mt-0.5">
                        <agent.icon className="w-3 h-3 text-yellow-500" />
                      </div>
                      <div>
                        <p className={`font-semibold text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{agent.name}</p>
                        <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{agent.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* The Result */}
              <div className={`px-6 py-3 ${isDarkMode ? 'bg-yellow-500/5' : 'bg-yellow-50'} border-b border-yellow-500/20`}>
                <p className={`text-xs font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  <span className="font-bold">THE RESULT:</span> More opportunities. Faster response times. More revenue.
                </p>
              </div>

              {/* Best For */}
              <div className={`px-6 py-3 ${isDarkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="font-semibold">Best for:</span> Businesses receiving consistent enquiries looking to increase sales.
                </p>
              </div>

              {/* CTA */}
              <div className="px-6 py-5">
                <Button
                  onClick={onBookNow}
                  className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-3 rounded-full text-sm transition-all transform hover:scale-[1.02] shadow-lg shadow-yellow-500/20"
                  data-testid="revenue-system-cta"
                >
                  Get Your Free AI Audit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12">
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Unlike generic AI software, we don't simply hand you a login.
          </p>
          <p className={`text-sm font-medium mt-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            We build, integrate, and continuously improve your AI systems.
          </p>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;
